import { Controller } from '@hotwired/stimulus'

/* Two roles, one controller, like beachy-modal:
   - on the panel scrim (rendered by BeachySidePanelComponent): open/dismiss/load/submit
   - on a trigger anywhere on the page: `show` looks the panel up by uuid and opens it,
     passing the trigger's data-side-panel-url / -title / -subtitle along.

   A fetched fragment may set data-side-panel-title / -title-html / -subtitle on its root
   element to retitle the head, and may carry a <template data-side-panel-footer> whose content
   becomes the pinned footer (an empty footer collapses). Links inside the body use
   `click->beachy-side-panel#load` with a data-side-panel-url; forms use
   data-side-panel-submit. */
export default class extends Controller {
  static targets = ['title', 'subtitle', 'body', 'close', 'footer']

  show(event) {
    event.preventDefault()
    const trigger = event.currentTarget
    const panel = this.panelFor(trigger.dataset.sidePanelUuid)
    if (!panel) return

    panel.open(trigger, trigger.dataset)
  }

  open(trigger, dataset) {
    this.trigger = trigger
    this.retitle(dataset)
    this.element.classList.remove('hidden')
    if (this.hasCloseTarget) this.closeTarget.focus()
    if (dataset.sidePanelUrl) this.fetchInto(dataset.sidePanelUrl)
  }

  load(event) {
    event.preventDefault()
    const { dataset } = event.currentTarget
    this.retitle(dataset)
    this.fetchInto(dataset.sidePanelUrl)
  }

  submit(event) {
    const form = event.target
    if (!form.hasAttribute('data-side-panel-submit')) return

    event.preventDefault()
    this.fetchInto(form.action, { method: form.method || 'post', body: new FormData(form) })
  }

  // A fetched body is dropped on close so its controllers disconnect; the next open refetches
  dismiss(event) {
    if (event) event.preventDefault()
    this.element.classList.add('hidden')
    if (this.trigger?.dataset.sidePanelUrl) {
      this.bodyTarget.replaceChildren()
      this.swapFooter(null)
    }
    if (this.trigger) this.trigger.focus()
    this.trigger = null
  }

  // Scrim click only: a click inside the panel bubbles here too and must not close it.
  dismissFromScrim(event) {
    if (event.target !== event.currentTarget) return

    this.dismiss(event)
  }

  async fetchInto(url, { method = 'get', body = null } = {}) {
    this.bodyTarget.classList.add('beachy-side-panel-loading')
    try {
      const response = await fetch(url, {
        method: method.toUpperCase(),
        body,
        headers: {
          Accept: 'text/html',
          'X-Requested-With': 'XMLHttpRequest',
          'X-CSRF-Token': this.csrfToken
        },
        credentials: 'same-origin'
      })
      // A finished action sends the browser on rather than a new body
      const redirect = response.headers.get('X-Side-Panel-Redirect')
      if (redirect) {
        window.location.assign(redirect)
        return
      }
      this.bodyTarget.innerHTML = await response.text()
      const root = this.bodyTarget.firstElementChild
      if (root) this.retitle(root.dataset)
      this.swapFooter(this.bodyTarget.querySelector('template[data-side-panel-footer]'))
    } finally {
      this.bodyTarget.classList.remove('beachy-side-panel-loading')
    }
  }

  swapFooter(template) {
    if (!this.hasFooterTarget) return

    this.footerTarget.replaceChildren()
    if (template) this.footerTarget.append(template.content.cloneNode(true))
  }

  // -title-html wins over -title when both are set; it is server-rendered markup
  retitle({ sidePanelTitle, sidePanelTitleHtml, sidePanelSubtitle }) {
    if (this.hasTitleTarget) {
      if (sidePanelTitleHtml !== undefined) this.titleTarget.innerHTML = sidePanelTitleHtml
      else if (sidePanelTitle !== undefined) this.titleTarget.textContent = sidePanelTitle
    }
    if (sidePanelSubtitle !== undefined && this.hasSubtitleTarget) this.subtitleTarget.textContent = sidePanelSubtitle
  }

  panelFor(uuid) {
    const element = document.getElementById(`side-panel-${uuid}`)
    if (!element) return null

    return this.application.getControllerForElementAndIdentifier(element, this.identifier)
  }

  get csrfToken() {
    return document.querySelector('meta[name="csrf-token"]')?.content || ''
  }
}
